import React from "react";
import { graphql, RelayPaginationProp } from "react-relay";

import {
  useLoadMore,
  useRefetch,
  withPaginationContainer,
} from "coral-framework/lib/relay";

import { SiteFilterContainer_query as QueryData } from "coral-admin/__generated__/SiteFilterContainer_query.graphql";
import { SiteFilterContainerPaginationQueryVariables } from "coral-admin/__generated__/SiteFilterContainerPaginationQuery.graphql";

import SiteFilter from "./SiteFilter";

import { PropTypesOf } from "coral-framework/types";
import StoryRowContainer from "../StoryRowContainer";

interface Props {
  query: QueryData | null;
  relay: RelayPaginationProp;
  siteID: string | null;
  onSelect: (id: string) => void;
  viewer: PropTypesOf<typeof StoryRowContainer>["viewer"] | null;
}

const SiteFilterContainer: React.FunctionComponent<Props> = (props, viewer) => {
  const viewerprops = props.viewer;
  let scoped = false;
  if(viewerprops && viewerprops.role && viewerprops.role == "MODERATOR" && viewerprops.moderationScopes && viewerprops.moderationScopes.scoped) {
    scoped = true;
  }
  const sites = scoped ? viewerprops.moderationScopes.sites : props.query
    ? props.query.sites.edges.map((edge) => edge.node)
    : [];
  
  // uncomment when site moderator doesn't need All sites
  /* 
  if(scoped && !props.siteID){
    props.onSelect(sites[0].id)
  }
  */
  
  const [loadMore, isLoadingMore] = useLoadMore(props.relay, 10);
  const [, isRefetching] = useRefetch<
    SiteFilterContainerPaginationQueryVariables
  >(props.relay);
  return (
    <SiteFilter
      onSelect={props.onSelect}
      loading={!props.query || isRefetching}
      sites={sites}
      siteID={props.siteID}
      onLoadMore={loadMore}
      hasMore={!isRefetching && props.relay.hasMore()}
      disableLoadMore={isLoadingMore}
      scoped={scoped}
    />
  );
};

type FragmentVariables = SiteFilterContainerPaginationQueryVariables;

const enhanced = withPaginationContainer<
  Props,
  SiteFilterContainerPaginationQueryVariables,
  FragmentVariables
>(
  {
    query: graphql`
      fragment SiteFilterContainer_query on Query
        @argumentDefinitions(
          count: { type: "Int!", defaultValue: 10 }
          cursor: { type: "Cursor" }
        ) {
        sites(first: $count, after: $cursor)
          @connection(key: "SitesConfig_sites") {
          edges {
            node {
              id
              ...SiteFilterOption_site
              ...SiteFilterSelected_site
            }
          }
        }
      }
    `,
  },
  {
    direction: "forward",
    getConnectionFromProps(props) {
      return props.query && props.query.sites;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
      # Pagination query to be fetched upon calling 'loadMore'.
      # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
      query SiteFilterContainerPaginationQuery($count: Int!, $cursor: Cursor) {
        ...SiteFilterContainer_query @arguments(count: $count, cursor: $cursor)
      }
    `,
  }
)(SiteFilterContainer);
export default enhanced;
